import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Lightbulb } from 'lucide-react';
import MarkdownMessage from '@/components/MarkdownMessage';
import { Spinner } from '@/components/ui/spinner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { opinionsApi, type OpinionArticlePublic } from '@/services/api';
import { OPINIONS_PATH } from '@/constants/appRoutes';

const PAGE_TYPE_LABELS: Record<string, string> = {
  topic: 'Topic',
  decision: 'Decision',
  entity: 'Entity',
};

function formatDate(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

/** Reader for one RCM opinion article (markdown from the LLM-wiki pipeline). */
export function OpinionArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const [article, setArticle] = useState<OpinionArticlePublic | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const result = await opinionsApi.getBySlug(slug);
        if (!cancelled) setArticle(result);
      } catch (e: unknown) {
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (!cancelled) {
          setError(status === 404 ? 'This article does not exist.' : 'Failed to load the article.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  return (
    <div className="mx-auto w-full max-w-3xl px-4 py-8">
      <Link
        to={OPINIONS_PATH}
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground no-underline mb-4"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        All RCM Opinions
      </Link>

      {loading ? (
        <div className="py-16 flex justify-center">
          <Spinner />
        </div>
      ) : error ? (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : article ? (
        <article>
          <header className="mb-6 border-b pb-4">
            <div className="flex items-center gap-1.5 flex-wrap mb-2">
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium bg-violet-100 text-violet-800">
                <Lightbulb className="h-3 w-3" />
                RCM Opinion
              </span>
              <span className="inline-flex px-1.5 py-0.5 rounded text-[11px] font-medium bg-slate-100 text-slate-700">
                {PAGE_TYPE_LABELS[article.page_type] ?? article.page_type}
              </span>
              {article.updated_at && (
                <span className="text-[11px] text-muted-foreground">
                  Updated {formatDate(article.updated_at)}
                </span>
              )}
            </div>
            <h1 className="font-display text-2xl font-semibold text-foreground">{article.title}</h1>
          </header>
          {/* Article bodies start with '# <title>' (wiki rule); drop that
              first heading since the header above already shows it. Tolerate
              leading whitespace and a missing trailing newline. */}
          <MarkdownMessage content={article.content.replace(/^\s*#\s[^\n]*\n*/, '')} />
        </article>
      ) : null}
    </div>
  );
}

export default OpinionArticlePage;
